"use client";

import { useEffect, useState } from "react";
import { useSession } from "next-auth/react";
import { useTranslations } from "next-intl";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

/**
 * Tarif affiché dans le hero (cadre noir) de la page mentor.
 *
 * La page est mise en cache (ISR), donc l'éligibilité par utilisateur ne peut
 * pas être calculée côté serveur : ce composant client la vérifie comme le
 * fait le bloc de réservation. On n'affiche « Offert » que si le mentor propose
 * la découverte ET :
 *   - le visiteur n'est pas connecté (incitation à l'inscription), ou
 *   - le membre connecté est réellement éligible.
 * Par défaut (chargement) on affiche le prix : un membre non éligible ne voit
 * jamais « Offert » à tort.
 */
export default function MentorHeroPrice({
    pricePerCall,
    mentorPseudo,
    allowsDiscovery = false,
}) {
    const t = useTranslations();
    const { status } = useSession();
    const [eligible, setEligible] = useState(false);

    useEffect(() => {
        let active = true;
        if (allowsDiscovery && status === "authenticated" && mentorPseudo) {
            fetch("/api/call/get/discovery-eligibility", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ mentorPseudo }),
            })
                .then((r) => r.json())
                .then((d) => {
                    if (active) setEligible(!!(d && d.available));
                })
                .catch(() => {
                    if (active) setEligible(false);
                });
        } else if (active) {
            setEligible(false);
        }
        return () => {
            active = false;
        };
    }, [allowsDiscovery, status, mentorPseudo]);

    if (!pricePerCall) return null;

    const showOffer =
        allowsDiscovery &&
        (status === "unauthenticated" ||
            (status === "authenticated" && eligible));

    if (showOffer) {
        return (
            <div>
                <div className="text-2xl font-bold">
                    {t("mentor_discovery_offered")}{" "}
                    <span className="text-base font-normal text-white/40 line-through">
                        {pricePerCall} €
                    </span>
                </div>
                <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white/10 backdrop-blur-sm px-2.5 py-1 text-xs font-semibold text-white">
                    <SolarIcon
                        name="gift-linear"
                        className="text-sm text-primary"
                    />
                    {t("GuestBooking_free")}
                </span>
            </div>
        );
    }

    return (
        <div className="text-2xl font-bold">
            {pricePerCall} €{" "}
            <span className="text-base font-normal text-white/55">/ appel</span>
        </div>
    );
}
